// Customer affiliate — BO pages (Phase 3 spec, mdf-project
// docs/specs/2026-10-13-phase3-affiliate-design.md §3.7, §3.8, §5).
//
// Pure helpers shared by src/views/Marketing/Affiliate*.vue (no Vue, no axios)
// so the rules the pages apply before asking the API are unit-tested.

export const COMMISSION_STATUSES = ["pending", "approved", "paid", "cancelled", "reversed"];
export const ZERO_REASONS = ["paid_ad", "self_purchase", "rule_zero", "no_rule"];
export const PROFILE_STATUSES = ["none", "submitted", "verified", "rejected"];
export const PAYOUT_STATUSES = ["draft", "submitted", "paid", "cancelled"];
export const RULE_SCOPES = ["default", "category", "product", "promotion"];
export const RULE_STATES = ["active", "scheduled", "expired", "disabled"];
export const MEMBER_STATUS_TABS = ["all", "active", "suspended", "deleted"];
export const BANKS = ["BBL", "KBANK", "KTB", "SCB", "BAY", "TTB", "GSB", "BAAC", "KKP", "CIMBT", "UOBT", "LHB", "TISCO", "ICBCT", "GHB"];
export const LIST_LIMIT = 20;

// el-tag types
export function commissionStatusTag(s) {
	return { pending: "warning", approved: "", paid: "success", cancelled: "info", reversed: "danger" }[s] || "info";
}
export function profileStatusTag(s) {
	return { none: "info", submitted: "warning", verified: "success", rejected: "danger" }[s] || "info";
}
export function payoutStatusTag(s) {
	return { draft: "info", submitted: "warning", paid: "success", cancelled: "danger" }[s] || "info";
}
export function ruleStateTag(s) {
	return { active: "success", scheduled: "warning", expired: "info", disabled: "danger" }[s] || "info";
}
export function memberStatusKey(row) {
	if (row && row.member_deleted) return "deleted";
	return row && row.status === "suspended" ? "suspended" : "active";
}

/** 1,234.50 — money with 2 decimals (th-TH grouping). */
export function money(n) {
	const v = Number(n);
	return (Number.isFinite(v) ? v : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Rate column (§5.2): `10%` / `฿20 ต่อชิ้น` (per-item word passed in for i18n). */
export function rateLabel(rateType, rate, perItem) {
	const v = Number(rate) || 0;
	const n = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
	if (rateType === "percent") return `${n}%`;
	if (rateType === "fixed") return `฿${n} ${perItem || ""}`.trim();
	return "-";
}

// ---------------------------------------------------------------------------
// Dates (Asia/Bangkok, inclusive ranges)
export function bkkDate(d = new Date()) {
	return new Date(d.getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10);
}
/** [from, to] for the last `days` days ending today (+07). */
export function defaultRange(days = 30, now = new Date()) {
	return [bkkDate(new Date(now.getTime() - (days - 1) * 86400 * 1000)), bkkDate(now)];
}
export function yesterday(now = new Date()) {
	return bkkDate(new Date(now.getTime() - 86400 * 1000));
}
/** ≤ maxDays inclusive and from ≤ to. */
export function rangeOk(range, maxDays = 366) {
	if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) return false;
	const a = Date.parse(range[0] + "T00:00:00Z");
	const b = Date.parse(range[1] + "T00:00:00Z");
	if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return false;
	return (b - a) / 86400000 + 1 <= maxDays;
}

// ---------------------------------------------------------------------------
// Settings (§3.7 PUT /settings)
export function settingsPayload(form, bumpTerms) {
	return {
		enabled: !!form.enabled,
		click_window_days: Number(form.click_window_days),
		maturity_days: Number(form.maturity_days),
		min_payout: Number(form.min_payout),
		wht_percent: Number(form.wht_percent),
		terms_th: String(form.terms_th || ""),
		terms_en: String(form.terms_en || ""),
		bump_terms_version: !!bumpTerms,
		expected_updated_at: form.updated_at || null,
	};
}
/** Field → rule for the client-side checks (mirrors the SQL CHECKs). */
export function settingsErrors(form) {
	const e = {};
	const int = (v) => Number.isInteger(Number(v)) && String(v) !== "";
	if (!int(form.click_window_days) || form.click_window_days < 1 || form.click_window_days > 30) e.click_window_days = "range";
	if (!int(form.maturity_days) || form.maturity_days < 0 || form.maturity_days > 90) e.maturity_days = "range";
	const m = Number(form.min_payout);
	if (String(form.min_payout) === "" || !Number.isFinite(m) || m < 0) e.min_payout = "range";
	return e;
}

// ---------------------------------------------------------------------------
// Rules (§3.7 RuleInput)
export function ruleNeedsTarget(scope) {
	return scope === "category" || scope === "product" || scope === "promotion";
}
export function rulePayload(form) {
	const scope = form.scope;
	return {
		scope,
		scope_id: ruleNeedsTarget(scope) ? Number(form.scope_id) || null : null,
		rate_type: form.rate_type,
		rate: Number(form.rate),
		starts_at: form.starts_at || null,
		ends_at: form.ends_at || null,
		note: String(form.note || "").trim(),
	};
}
export function ruleErrors(form) {
	const e = {};
	if (RULE_SCOPES.indexOf(form.scope) === -1) e.scope = "required";
	if (ruleNeedsTarget(form.scope) && !(Number(form.scope_id) > 0)) e.scope_id = "required";
	if (form.rate_type !== "percent" && form.rate_type !== "fixed") e.rate_type = "required";
	const r = Number(form.rate);
	if (String(form.rate) === "" || form.rate === null || !Number.isFinite(r) || r < 0) e.rate = "range";
	else if (form.rate_type === "percent" && r > 50) e.rate = "range";
	else if (form.rate_type === "fixed" && r > 10000) e.rate = "range";
	if (form.starts_at && form.ends_at && Date.parse(form.ends_at) <= Date.parse(form.starts_at)) e.ends_at = "range";
	if (String(form.note || "").length > 300) e.note = "max_length";
	return e;
}
/** No ACTIVE default rule → the warning line (§5.2). */
export function hasActiveDefault(rules) {
	return (rules || []).some((r) => r.scope === "default" && r.state === "active");
}

// ---------------------------------------------------------------------------
// Query builders (drop empty values)
function clean(o) {
	const out = {};
	Object.keys(o).forEach((k) => {
		const v = o[k];
		if (v === "" || v === null || v === undefined || v === false) return;
		out[k] = v;
	});
	return out;
}
export function memberQuery(f, limit = LIST_LIMIT) {
	return clean({
		q: (f.q || "").trim(),
		status: f.status && f.status !== "all" ? f.status : "",
		profile_status: f.profile_status || "",
		hold: f.hold ? "true" : "",
		page: f.page || 1,
		limit,
	});
}
export function commissionQuery(f, limit = LIST_LIMIT) {
	const r = Array.isArray(f.range) ? f.range : [];
	return clean({
		from: r[0] || "",
		to: r[1] || "",
		status: f.status || "",
		zero_reason: f.zero_reason || "",
		kind: f.kind || "",
		affiliate_member_id: f.affiliate_member_id || "",
		code: (f.code || "").trim().toUpperCase(),
		order_no: (f.order_no || "").trim(),
		payout_id: f.payout_id || "",
		page: f.page || 1,
		limit,
	});
}

/**
 * Ledger deep link (?code= / ?payout_id=, from the member drawer and the payout
 * detail): when the URL carries EITHER key it is the whole deep-link filter —
 * each field takes the URL value or "" — so a filter from an earlier link can
 * never survive into the next one. No key → null (keep the screen's filters).
 */
export const LEDGER_LINK_KEYS = ["code", "payout_id"];
export function ledgerLinkFilters(query) {
	const q = query || {};
	if (!LEDGER_LINK_KEYS.some((k) => q[k] !== undefined && q[k] !== null)) return null;
	const out = {};
	LEDGER_LINK_KEYS.forEach((k) => (out[k] = q[k] === undefined || q[k] === null ? "" : String(q[k])));
	return out;
}

/** Landing of the /marketing parent route: affiliate members for a finance-only user. */
export function marketingHomePath(getters) {
	const g = getters || {};
	if (!g["Auth/isAccessMarketing"] && g["Auth/isAccessAffiliateFinance"]) return "/marketing/affiliate/members";
	return "/marketing/landing-pages";
}

// ---------------------------------------------------------------------------
// Adjustments (§3.7 POST /adjustments)
export function adjustErrors(form) {
	const e = {};
	if (!(Number(form.affiliate_member_id) > 0)) e.affiliate_member_id = "required";
	const s = String(form.amount == null ? "" : form.amount).trim();
	const a = Number(s);
	if (!s || !/^-?\d+(\.\d{1,2})?$/.test(s) || a === 0 || a < -100000 || a > 100000) e.amount = "range";
	const note = String(form.note || "").trim();
	if (!note) e.note = "required";
	else if (note.length > 500) e.note = "max_length";
	return e;
}
export function adjustPayload(form) {
	const out = {
		affiliate_member_id: Number(form.affiliate_member_id),
		amount: Number(String(form.amount).trim()),
		note: String(form.note || "").trim(),
	};
	const no = String(form.order_no || "").trim();
	if (no) out.order_no = no;
	return out;
}

// ---------------------------------------------------------------------------
// Commissions
/** "ยกเลิกค่าคอม" is offered only for pending/approved order rows not in a payout (§3.7). */
export function canCancelCommission(row) {
	return !!row && row.kind === "order" && (row.status === "pending" || row.status === "approved") && !row.payout_code;
}
/** `paid_touch` tooltip text: source / medium / campaign · time. */
export function paidTouchText(t, fmt) {
	if (!t || typeof t !== "object") return "";
	const parts = [t.source, t.medium, t.campaign].filter(Boolean).join(" / ");
	const ts = Number(t.ts);
	const when = Number.isFinite(ts) && ts > 0 && fmt ? fmt(new Date(ts * 1000).toISOString()) : "";
	return [parts, when].filter(Boolean).join(" · ");
}

// ---------------------------------------------------------------------------
// Payouts
/** Actions per round status (§5.5). */
export function payoutActions(p) {
	const s = p && p.status;
	return {
		submit: s === "draft",
		cancel: s === "draft" || (s === "submitted" && Number(p.paid_count) === 0),
		csv: s === "submitted" || s === "paid",
		removeItem: s === "draft",
		pay: s === "submitted",
	};
}
export function itemActions(payout, item) {
	const ps = payout && payout.status;
	return {
		remove: ps === "draft",
		pay: ps === "submitted" && item.status === "unpaid",
		viewSlip: !!item.has_slip,
		unpay: item.status === "paid",
	};
}
export const SLIP_MAX_BYTES = 10 * 1024 * 1024;
export function slipOk(file) {
	if (!file || file.size <= 0 || file.size > SLIP_MAX_BYTES) return false;
	if (["image/jpeg", "image/png", "application/pdf"].indexOf(file.type) !== -1) return true;
	return !file.type && /\.(jpe?g|png|pdf)$/i.test(file.name || "");
}
export function payFormData(form, file) {
	const fd = new FormData();
	fd.append("payment_date", form.payment_date);
	fd.append("slip", file);
	if (form.payment_note) fd.append("payment_note", String(form.payment_note).trim());
	return fd;
}

// ---------------------------------------------------------------------------
// Report
export const REPORT_CARDS = [
	"affiliates_active", "affiliates_with_sales", "clicks", "orders", "base_total",
	"commission_pending", "commission_approved", "commission_paid",
	"zero_paid_ad_orders", "zero_self_orders", "reversed_total",
];
const MONEY_CARDS = ["base_total", "commission_pending", "commission_approved", "commission_paid", "reversed_total"];
export function reportCardValue(key, cards) {
	const v = cards ? Number(cards[key]) || 0 : 0;
	return MONEY_CARDS.indexOf(key) !== -1 ? money(v) : v.toLocaleString("en-US");
}
export function reportChart(series, labels) {
	const s = Array.isArray(series) ? series : [];
	return {
		labels: s.map((r) => r.date),
		datasets: [
			{ label: labels.clicks, data: s.map((r) => Number(r.clicks) || 0), borderColor: "#5e72e4", fill: false },
			{ label: labels.orders, data: s.map((r) => Number(r.orders) || 0), borderColor: "#fb6340", fill: false },
		],
	};
}

// ---------------------------------------------------------------------------
// Blob download
export function saveBlob(res, filename) {
	const data = res && res.data;
	const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Per-member eligibility (owner 2026-10-16): only members BO grants see the
// Affiliate menu / may use the program.
// ---------------------------------------------------------------------------

export const ELIGIBILITY_NOTE_MAX = 300;
export const ELIGIBILITY_FILTERS = ["true", "false", "all"];

/** "eligible" | "revoked" (granted before, now off) | "never" */
export function eligibilityKey(e) {
	if (!e) return "never";
	if (e.eligible === true) return "eligible";
	return e.revoked_at || e.granted_at ? "revoked" : "never";
}
export function eligibilityTag(key) {
	return { eligible: "success", revoked: "danger", never: "info" }[key] || "info";
}
/** null when the note is acceptable, else the i18n suffix of the problem. */
export function eligibilityNoteError(note) {
	return String(note || "").trim().length > ELIGIBILITY_NOTE_MAX ? "note_too_long" : null;
}
/** list params: drop empties, eligible defaults to "true", limit ≤ 100. */
export function eligibilityQuery(f, limit) {
	const out = { page: f.page || 1, limit: Math.min(Number(limit) || LIST_LIMIT, 100), eligible: ELIGIBILITY_FILTERS.includes(String(f.eligible)) ? String(f.eligible) : "true" };
	const q = String(f.q || "").trim();
	if (q) out.q = q;
	return out;
}
